import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useCallback, useEffect, useState } from 'react';
import scrollIntoView from 'scroll-into-view';

import { PaymentMethods, PaymentProviders } from '@/enums';
import { ShippingMethods } from '@/enums/shippingMethod';
import { usePrevious } from '@/hooks/usePrevious';
import { ShippingMethod } from '@/pages/Checkout/components/Steps/ShippingMethod/ShippingMethod';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getCatalogAddress } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchInvoiceIfNeeded, submitShippingMethod } from '@/redux/modules/checkout/checkout.actions';
import {
    loadCheckoutShippingQuote,
    submitCheckoutShippingQuote,
} from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.actions';
import { SubmitCheckoutShippingQuoteInput } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.types';
import { fetchProcessingFeesIfNeeded } from '@/redux/modules/processingFee';
import { updateTaxAddress } from '@/redux/modules/taxes';
import { BankAccount } from '@/types/bankAccount';
import { BidderAddress } from '@/types/BidderAddresses';
import { CreditCard } from '@/types/CreditCard';
import { InvoiceItem } from '@/types/Invoice';
import { ShippingQuote } from '@/types/ShippingQuote';
import { getSellerUrl } from '@/utils/urls';

import { CheckoutStep } from './CheckoutStep';
import { Payment } from './Payment/Payment';
import Shipping from './Shipping/Shipping';
import { CheckoutProcessStep } from './types/CheckoutProcessStep.enum';

type StepsProps = {
    amountToPayZip: number;
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency: string;
    hadZipError: boolean;
    invoiceId: number;
    isNewCheckout: boolean;
    items: InvoiceItem[];
    laShipping: boolean;
    onBankAccountSelectionChange: (bankAccount: BankAccount) => void;
    onCardSelectionChange: (creditCard: CreditCard) => void;
    onPaymentMethodChange: (paymentMethod: PaymentMethods) => void;
    onShippingAddressChanged: (address: BidderAddress, valid: boolean) => void;
    onShippingMethodChange: (shippingMethod: ShippingMethods, shippingMethodValid: boolean) => void;
    paymentMethod: PaymentMethods;
    paymentMethodValid: boolean;
    paymentProvider: PaymentProviders;
    readOnly: boolean;
    requireACHForCheckout: boolean;
    selectedBankAccount: BankAccount;
    selectedCreditCard: CreditCard;
    setOnReviewStep: (onReviewStep: boolean) => void;
    shippingAddress: BidderAddress;
    shippingAddressValid: boolean;
    shippingAmount: number;
    shippingMethod: ShippingMethods;
    shippingMethodValid: boolean;
    shippingQuote: ShippingQuote;
    showAchPaymentOption: boolean;
    showCardPaymentOption: boolean;
    showZipOption: boolean;
};

const Steps = ({
    amountToPayZip,
    bidderPaysProcessingFee,
    catalogId,
    currency,
    hadZipError,
    invoiceId,
    isNewCheckout,
    items,
    laShipping,
    onBankAccountSelectionChange,
    onCardSelectionChange,
    onPaymentMethodChange,
    onShippingAddressChanged,
    onShippingMethodChange,
    paymentMethod,
    paymentMethodValid,
    readOnly,
    requireACHForCheckout,
    selectedBankAccount,
    selectedCreditCard,
    setOnReviewStep,
    shippingAddress,
    shippingAddressValid,
    shippingAmount,
    shippingMethod,
    shippingMethodValid,
    shippingQuote,
    showAchPaymentOption,
    showCardPaymentOption,
    showZipOption,
}: StepsProps) => {
    const dispatch = useAppDispatch();
    const catalogAddress = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const bidderId = useAppSelector((state) => getBidderId(state));
    const [step, setStep] = useState<CheckoutProcessStep>(CheckoutProcessStep.Shipping);
    const [isLoading, setIsLoading] = useState(false);
    const previousHadZipError = usePrevious(hadZipError);

    const shippingRef = React.useRef<HTMLDivElement>(null);
    const shippingMethodRef = React.useRef<HTMLDivElement>(null);
    const paymentRef = React.useRef<HTMLDivElement>(null);

    const { sellerId: houseId, sellerName } = items?.[0] || {};
    const sellerUrl = getSellerUrl(Number(houseId), sellerName);

    //ensures that the bidders address is not updated to the house address when they selece house pick up wit no default address.
    const skipProfileUpdate = true;

    const changeStepToShipping = useCallback(() => {
        setStep(CheckoutProcessStep.Shipping);
        scrollIntoView(shippingRef.current, {
            align: { top: 0, topOffset: 24 },
        });
        setOnReviewStep(false);
    }, [setOnReviewStep]);

    const changeStepToShippingMethod = useCallback(() => {
        setStep(CheckoutProcessStep.ShippingMethod);
        scrollIntoView(shippingMethodRef.current, {
            align: { top: 0, topOffset: 24 },
        });
        setOnReviewStep(false);
    }, [setOnReviewStep]);

    const changeStepToPayment = useCallback(() => {
        setStep(CheckoutProcessStep.Payment);
        scrollIntoView(paymentRef.current, {
            align: { top: 0, topOffset: 24 },
        });
        setOnReviewStep(false);
    }, [setOnReviewStep]);

    const changeStepToReview = useCallback(() => {
        if (!readOnly) {
        }
        setStep(CheckoutProcessStep.Review);
        setOnReviewStep(true);
    }, [readOnly, setOnReviewStep]);

    const completeShippingStep = useCallback(async () => {
        //it isn't normally possible to edit the address after the shipping method is set to pickup
        //but if the user refreshes the page after selecting pickup, then they can edit address. In which case just default back to bidder shipping.
        if (isNewCheckout) {
            // default to LiveShipping if available, fall back to bidder shipping
            if (laShipping) {
                onShippingMethodChange(ShippingMethods.LiveShipping, true);
            } else {
                onShippingMethodChange(ShippingMethods.BidderShipping, true);
            }
        }
        setIsLoading(true);
        await dispatch(
            updateTaxAddress(catalogId, invoiceId, shippingAddress, shippingAddress.phoneNumber, skipProfileUpdate)
        );
        await dispatch(fetchInvoiceIfNeeded({ catalogId, force: true }));
        // if the shipping step has been completed then we need to get the shipping quote
        await dispatch(loadCheckoutShippingQuote(invoiceId));
        setIsLoading(false);
        changeStepToShippingMethod();
    }, [
        isNewCheckout,
        dispatch,
        catalogId,
        invoiceId,
        shippingAddress,
        skipProfileUpdate,
        changeStepToShippingMethod,
        laShipping,
        onShippingMethodChange,
    ]);

    const completeShippingMethodStep = useCallback(async () => {
        setIsLoading(true);
        await dispatch(submitShippingMethod({ invoiceId, shippingMethod }));
        let input: SubmitCheckoutShippingQuoteInput = {
            amount: shippingAmount,
            bidderId: bidderId,
            invoiceId: invoiceId,
            usingLaShipping: false,
        };
        if (shippingMethod === ShippingMethods.LiveShipping) {
            input.amount = Number(shippingQuote.total);
            input.usingLaShipping = true;
        }
        await dispatch(submitCheckoutShippingQuote(input));
        if (Boolean(shippingAddress?.addressId) || shippingMethod === ShippingMethods.Pickup) {
            const taxAddress =
                shippingMethod === ShippingMethods.Pickup
                    ? { ...catalogAddress, countryCode: 'US', phoneNumber: '' }
                    : shippingAddress;
            await dispatch(
                updateTaxAddress(catalogId, invoiceId, taxAddress, taxAddress.phoneNumber || '', skipProfileUpdate)
            );
        }
        await dispatch(fetchInvoiceIfNeeded({ catalogId, force: true }));
        await dispatch(fetchProcessingFeesIfNeeded(catalogId, true));

        setIsLoading(false);
        changeStepToPayment();
    }, [
        bidderId,
        catalogAddress,
        catalogId,
        changeStepToPayment,
        dispatch,
        invoiceId,
        shippingAddress,
        shippingAmount,
        shippingMethod,
        shippingQuote.total,
        skipProfileUpdate,
    ]);

    const completePaymentStep = useCallback(() => {
        changeStepToReview();
    }, [changeStepToReview]);

    useEffect(() => {
        if (readOnly && step !== CheckoutProcessStep.Review) {
            changeStepToReview();
        }
    }, [changeStepToReview, readOnly, step]);

    useEffect(() => {
        if (!previousHadZipError && hadZipError && step === CheckoutProcessStep.Review) {
            changeStepToPayment();
        }
    }, [changeStepToPayment, hadZipError, previousHadZipError, step]);

    return (
        <div>
            <CheckoutStep
                activeStep={step}
                loading={isLoading}
                onEdit={shippingMethod !== ShippingMethods.Pickup && changeStepToShipping}
                onSave={completeShippingStep}
                readOnly={readOnly}
                saveButtonText={<FormattedMessage id="checkout.shipping.save" />}
                step={CheckoutProcessStep.Shipping}
                stepRef={shippingRef}
                title={<FormattedMessage id="checkout.shipping.title" />}
                valid={shippingAddressValid}
            >
                <Shipping
                    active={step === CheckoutProcessStep.Shipping}
                    addressId={shippingAddress?.addressId}
                    onReviewStep={step === CheckoutProcessStep.Review}
                    onShippingAddressChanged={onShippingAddressChanged}
                    shippingMethod={shippingMethod}
                />
            </CheckoutStep>
            <CheckoutStep
                activeStep={step}
                loading={isLoading}
                onEdit={changeStepToShippingMethod}
                onSave={completeShippingMethodStep}
                readOnly={readOnly}
                saveButtonText={<FormattedMessage id="checkout.shippingMethod.save" />}
                step={CheckoutProcessStep.ShippingMethod}
                stepRef={shippingMethodRef}
                title={<FormattedMessage id="checkout.shippingMethod.title" />}
                valid={readOnly || shippingMethodValid}
            >
                <ShippingMethod
                    active={step === CheckoutProcessStep.ShippingMethod}
                    catalogId={catalogId}
                    laShipping={laShipping}
                    loading={isLoading}
                    onReviewStep={step === CheckoutProcessStep.Review}
                    onShippingMethodChange={onShippingMethodChange}
                    sellerName={sellerName}
                    sellerUrl={sellerUrl}
                    shippingMethod={shippingMethod}
                    shippingMethodValid={shippingMethodValid}
                    shippingQuote={shippingQuote}
                />
            </CheckoutStep>
            <CheckoutStep
                activeStep={step}
                loading={isLoading}
                onEdit={changeStepToPayment}
                onSave={completePaymentStep}
                readOnly={readOnly}
                saveButtonText={<FormattedMessage id="checkout.payment.save" />}
                step={CheckoutProcessStep.Payment}
                stepRef={paymentRef}
                title={<FormattedMessage id="checkout.payment.title" />}
                valid={readOnly || paymentMethodValid}
            >
                <Payment
                    active={step === CheckoutProcessStep.Payment}
                    amountToPayZip={amountToPayZip}
                    bidderPaysProcessingFee={bidderPaysProcessingFee}
                    catalogId={catalogId}
                    currency={currency}
                    hadZipError={hadZipError}
                    onBankAccountSelectionChange={onBankAccountSelectionChange}
                    onCardSelectionChange={onCardSelectionChange}
                    onPaymentMethodChange={onPaymentMethodChange}
                    paymentMethod={paymentMethod}
                    readOnly={readOnly}
                    requireACHForCheckout={requireACHForCheckout}
                    selectedBankAccount={selectedBankAccount}
                    selectedCreditCard={selectedCreditCard}
                    showAchPaymentOption={showAchPaymentOption}
                    showCardPaymentOption={showCardPaymentOption}
                    showZipOption={showZipOption}
                />
            </CheckoutStep>
        </div>
    );
};

export default Steps;
